import type { UniversityProgram } from './universityData'

// Carrington College program catalog. Each program has a stable program ID
// (per advertiser spec) and a user-facing name + slug.
export interface CarringtonProgramDef {
  id: number
  name: string
  degreeLevel: string
  category: string
  slug: string
}

// Unique programs across all campuses (deduped by name).
// Note: Veterinary Technology has multiple program IDs depending on campus
// (540 at Pleasant Hill / Sacramento / San Jose / San Leandro / Stockton,
// 1665 at Ontario). The user-facing program is the same — campus context
// determines which underlying ID is posted.
export const CARRINGTON_PROGRAMS: CarringtonProgramDef[] = [
  {
    id: 245,
    name: 'Dental Assisting',
    degreeLevel: 'Diploma',
    category: 'Healthcare',
    slug: 'dental-assisting',
  },
  {
    id: 246,
    name: 'Massage Therapy',
    degreeLevel: 'Diploma',
    category: 'Healthcare',
    slug: 'massage-therapy',
  },
  {
    id: 247,
    name: 'Medical Assisting',
    degreeLevel: 'Diploma',
    category: 'Healthcare',
    slug: 'medical-assisting',
  },
  {
    id: 248,
    name: 'Medical Billing and Coding',
    degreeLevel: 'Diploma',
    category: 'Healthcare',
    slug: 'medical-billing-and-coding',
  },
  {
    id: 249,
    name: 'Pharmacy Technology',
    degreeLevel: 'Diploma',
    category: 'Healthcare',
    slug: 'pharmacy-technology',
  },
  {
    id: 250,
    name: 'Veterinary Assistant',
    degreeLevel: 'Diploma',
    category: 'Healthcare',
    slug: 'veterinary-assistant',
  },
  {
    id: 256,
    name: 'Medical Administrative Assistant',
    degreeLevel: 'Diploma',
    category: 'Healthcare',
    slug: 'medical-administrative-assistant',
  },
  {
    id: 540,
    name: 'Veterinary Technology',
    degreeLevel: 'Diploma',
    category: 'Healthcare',
    slug: 'veterinary-technology',
  },
  {
    id: 1664,
    name: 'Medical Office Administration',
    degreeLevel: 'Undergraduate',
    category: 'Healthcare',
    slug: 'medical-office-administration',
  },
  {
    id: 1676,
    name: 'Respiratory Care',
    degreeLevel: 'Diploma',
    category: 'Healthcare',
    slug: 'respiratory-care',
  },
]

// Quick lookup helpers
const PROGRAM_BY_ID: Record<number, CarringtonProgramDef> =
  CARRINGTON_PROGRAMS.reduce(
    (acc, p) => {
      acc[p.id] = p
      return acc
    },
    {} as Record<number, CarringtonProgramDef>,
  )

// Note: 1665 (Ontario) is Veterinary Technology — maps to the same
// user-facing program as 540.
const ALIAS_PROGRAM_IDS: Record<number, number> = {
  1665: 540,
}

const resolveProgramId = (id: number) => ALIAS_PROGRAM_IDS[id] ?? id

// Campus list with the program IDs available at each campus
export interface CarringtonCampusDef {
  id: number
  name: string
  programIds: number[]
}

export const CARRINGTON_CAMPUSES: CarringtonCampusDef[] = [
  {
    id: 49,
    name: 'Albuquerque',
    programIds: [245, 247, 249],
  },
  {
    id: 195,
    name: 'Bakersfield',
    programIds: [1664, 245, 247, 249, 250],
  },
  {
    id: 50,
    name: 'Boise',
    programIds: [245, 246, 247, 249],
  },
  {
    id: 196,
    name: 'Fresno',
    programIds: [1664, 245, 247, 249],
  },
  {
    id: 52,
    name: 'Glendale Center',
    programIds: [245, 247],
  },
  {
    id: 197,
    name: 'Hesperia',
    programIds: [1664, 245, 247, 249],
  },
  {
    id: 198,
    name: 'Lancaster',
    programIds: [1664, 247, 249],
  },
  {
    id: 53,
    name: 'Las Vegas',
    programIds: [245, 247],
  },
  {
    id: 54,
    name: 'Mesa',
    programIds: [245, 247, 249],
  },
  {
    id: 199,
    name: 'Modesto',
    programIds: [1664, 247, 249],
  },
  {
    id: 55,
    name: 'Online',
    programIds: [256, 248, 249],
  },
  {
    id: 200,
    name: 'Ontario',
    programIds: [247, 249, 1665],
  },
  {
    id: 56,
    name: 'Phoenix',
    programIds: [245, 247, 249, 1676, 250],
  },
  {
    id: 58,
    name: 'Pleasant Hill',
    programIds: [245, 247, 249, 540],
  },
  {
    id: 59,
    name: 'Portland',
    programIds: [245, 247, 250],
  },
  {
    id: 201,
    name: 'Rancho Mirage',
    programIds: [1664, 247, 249],
  },
  {
    id: 60,
    name: 'Reno',
    programIds: [247],
  },
  {
    id: 61,
    name: 'Sacramento',
    programIds: [245, 247, 249, 540],
  },
  {
    id: 62,
    name: 'San Jose',
    programIds: [245, 247, 249, 540],
  },
  {
    id: 63,
    name: 'San Leandro',
    programIds: [245, 247, 249, 540],
  },
  {
    id: 202,
    name: 'Santa Maria',
    programIds: [1664, 247],
  },
  {
    id: 64,
    name: 'Spokane',
    programIds: [245, 246, 247, 249, 250],
  },
  {
    id: 65,
    name: 'Stockton',
    programIds: [245, 247, 249, 540],
  },
  {
    id: 203,
    name: 'Temecula',
    programIds: [1664, 245, 247, 249],
  },
  {
    id: 66,
    name: 'Tucson',
    programIds: [245, 247, 249, 250],
  },
  {
    id: 204,
    name: 'Visalia',
    programIds: [1664, 245, 247, 249],
  },
]

// Full ZIP → campus ID service-area map provided by Carrington College.
// Note: where ZIPs overlap between campuses, the later assignment wins
// (matches the order in the partner's data).
export const CARRINGTON_ZIP_TO_CAMPUS: Record<string, number> = {
  // Portland, OR (59)
  '97002': 59,
  '97004': 59,
  '97005': 59,
  '97006': 59,
  '97007': 59,
  '97008': 59,
  '97009': 59,
  '97013': 59,
  '97015': 59,
  '97017': 59,
  '97018': 59,
  '97019': 59,
  '97022': 59,
  '97023': 59,
  '97024': 59,
  '97027': 59,
  '97030': 59,
  '97032': 59,
  '97034': 59,
  '97035': 59,
  '97036': 59,
  '97042': 59,
  '97045': 59,
  '97051': 59,
  '97053': 59,
  '97055': 59,
  '97056': 59,
  '97060': 59,
  '97062': 59,
  '97068': 59,
  '97070': 59,
  '97071': 59,
  '97075': 59,
  '97076': 59,
  '97077': 59,
  '97080': 59,
  '97086': 59,
  '97089': 59,
  '97106': 59,
  '97109': 59,
  '97113': 59,
  '97115': 59,
  '97116': 59,
  '97119': 59,
  '97123': 59,
  '97124': 59,
  '97125': 59,
  '97127': 59,
  '97132': 59,
  '97133': 59,
  '97137': 59,
  '97140': 59,
  '97148': 59,
  '97201': 59,
  '97202': 59,
  '97203': 59,
  '97204': 59,
  '97205': 59,
  '97206': 59,
  '97207': 59,
  '97208': 59,
  '97209': 59,
  '97210': 59,
  '97211': 59,
  '97212': 59,
  '97213': 59,
  '97214': 59,
  '97215': 59,
  '97216': 59,
  '97217': 59,
  '97218': 59,
  '97219': 59,
  '97220': 59,
  '97221': 59,
  '97222': 59,
  '97223': 59,
  '97224': 59,
  '97225': 59,
  '97227': 59,
  '97228': 59,
  '97229': 59,
  '97230': 59,
  '97231': 59,
  '97232': 59,
  '97233': 59,
  '97236': 59,
  '97238': 59,
  '97239': 59,
  '97240': 59,
  '97242': 59,
  '97251': 59,
  '97253': 59,
  '97254': 59,
  '97255': 59,
  '97258': 59,
  '97266': 59,
  '97267': 59,
  '97268': 59,
  '97269': 59,
  '97271': 59,

  // Sacramento, CA (61)
  '94203': 61,
  '94205': 61,
  '94206': 61,
  '94207': 61,
  '94208': 61,
  '94209': 61,
  '94211': 61,
  '94229': 61,
  '94230': 61,
  '94232': 61,
  '94234': 61,
  '94235': 61,
  '94236': 61,
  '94237': 61,
  '94239': 61,
  '94240': 61,
  '94244': 61,
  '94246': 61,
  '94247': 61,
  '94248': 61,
  '94249': 61,
  '94250': 61,
  '94252': 61,
  '94254': 61,
  '94256': 61,
  '94257': 61,
  '94258': 61,
  '94259': 61,
  '94261': 61,
  '94262': 61,
  '94263': 61,
  '94267': 61,
  '94268': 61,
  '94269': 61,
  '94271': 61,
  '94273': 61,
  '94274': 61,
  '94277': 61,
  '94278': 61,
  '94279': 61,
  '94280': 61,
  '94282': 61,
  '94283': 61,
  '94284': 61,
  '94285': 61,
  '94286': 61,
  '94287': 61,
  '94288': 61,
  '94289': 61,
  '94290': 61,
  '94291': 61,
  '94293': 61,
  '94294': 61,
  '94295': 61,
  '94296': 61,
  '94297': 61,
  '95605': 61,
  '95608': 61,
  '95612': 61,
  '95615': 61,
  '95616': 61,
  '95617': 61,
  '95618': 61,
  '95620': 61,
  '95624': 61,
  '95626': 61,
  '95632': 61,
  '95638': 61,
  '95648': 61,
  '95655': 61,
  '95669': 61,
  '95670': 61,
  '95673': 61,
  '95683': 61,
  '95690': 61,
  '95691': 61,
  '95693': 61,
  '95695': 61,
  '95742': 61,
  '95747': 61,
  '95757': 61,
  '95758': 61,
  '95776': 61,
  '95798': 61,
  '95799': 61,
  '95812': 61,
  '95815': 61,
  '95820': 61,
  '95821': 61,
  '95822': 61,
  '95823': 61,
  '95824': 61,
  '95825': 61,
  '95826': 61,
  '95827': 61,
  '95828': 61,
  '95829': 61,
  '95831': 61,
  '95832': 61,
  '95833': 61,
  '95834': 61,
  '95835': 61,
  '95838': 61,
  '95843': 61,
  '95864': 61,

  // Albuquerque, NM (49)
  '87001': 49,
  '87004': 49,
  '87008': 49,
  '87015': 49,
  '87022': 49,
  '87031': 49,
  '87042': 49,
  '87043': 49,
  '87047': 49,
  '87048': 49,
  '87053': 49,
  '87059': 49,
  '87060': 49,
  '87068': 49,
  '87101': 49,
  '87102': 49,
  '87103': 49,
  '87104': 49,
  '87105': 49,
  '87106': 49,
  '87107': 49,
  '87108': 49,
  '87109': 49,
  '87110': 49,
  '87111': 49,
  '87112': 49,
  '87113': 49,
  '87114': 49,
  '87115': 49,
  '87116': 49,
  '87117': 49,
  '87119': 49,
  '87120': 49,
  '87121': 49,
  '87122': 49,
  '87123': 49,
  '87124': 49,
  '87125': 49,
  '87131': 49,
  '87144': 49,
  '87151': 49,
  '87153': 49,
  '87154': 49,
  '87158': 49,
  '87174': 49,
  '87176': 49,
  '87181': 49,
  '87184': 49,
  '87185': 49,
  '87187': 49,
  '87190': 49,
  '87191': 49,
  '87192': 49,
  '87193': 49,
  '87194': 49,
  '87195': 49,
  '87196': 49,
  '87197': 49,
  '87198': 49,
  '87199': 49,

  // Boise, ID (50)
  '83605': 50,
  '83606': 50,
  '83607': 50,
  '83616': 50,
  '83617': 50,
  '83626': 50,
  '83629': 50,
  '83630': 50,
  '83631': 50,
  '83634': 50,
  '83636': 50,
  '83639': 50,
  '83641': 50,
  '83642': 50,
  '83644': 50,
  '83651': 50,
  '83652': 50,
  '83653': 50,
  '83656': 50,
  '83666': 50,
  '83669': 50,
  '83670': 50,
  '83680': 50,
  '83686': 50,
  '83687': 50,
  '83701': 50,
  '83702': 50,
  '83703': 50,
  '83704': 50,
  '83705': 50,
  '83706': 50,
  '83707': 50,
  '83708': 50,
  '83709': 50,
  '83711': 50,
  '83712': 50,
  '83713': 50,
  '83714': 50,
  '83715': 50,
  '83716': 50,
  '83717': 50,
  '83719': 50,
  '83720': 50,
  '83721': 50,
  '83722': 50,
  '83724': 50,
  '83725': 50,
  '83726': 50,
  '83727': 50,
  '83728': 50,
  '83729': 50,
  '83730': 50,
  '83731': 50,
  '83732': 50,
  '83733': 50,
  '83735': 50,
  '83756': 50,
  '83799': 50,

  // Glendale Center, AZ (52)
  '85001': 52,
  '85002': 52,
  '85003': 52,
  '85004': 52,
  '85005': 52,
  '85006': 52,
  '85007': 52,
  '85008': 52,
  '85009': 52,
  '85010': 52,
  '85011': 52,
  '85012': 52,
  '85013': 52,
  '85014': 52,
  '85015': 52,
  '85016': 52,
  '85017': 52,
  '85018': 52,
  '85019': 52,
  '85020': 52,
  '85021': 52,
  '85022': 52,
  '85023': 52,
  '85024': 52,
  '85026': 52,
  '85027': 52,
  '85028': 52,
  '85029': 52,
  '85030': 52,
  '85031': 52,
  '85032': 52,
  '85033': 52,
  '85034': 52,
  '85035': 52,
  '85036': 52,
  '85037': 52,
  '85038': 52,
  '85039': 52,
  '85040': 52,
  '85041': 52,
  '85042': 52,
  '85043': 52,
  '85045': 52,
  '85046': 52,
  '85048': 52,
  '85051': 52,
  '85053': 52,
  '85060': 52,
  '85061': 52,
  '85062': 52,
  '85063': 52,
  '85064': 52,
  '85065': 52,
  '85066': 52,
  '85067': 52,
  '85068': 52,
  '85069': 52,
  '85070': 52,
  '85071': 52,
  '85072': 52,
  '85073': 52,
  '85074': 52,
  '85075': 52,
  '85076': 52,
  '85078': 52,
  '85079': 52,
  '85080': 52,
  '85082': 52,
  '85083': 52,
  '85085': 52,
  '85253': 52,
  '85301': 52,
  '85302': 52,
  '85303': 52,
  '85304': 52,
  '85305': 52,
  '85306': 52,
  '85307': 52,
  '85308': 52,
  '85309': 52,
  '85310': 52,
  '85311': 52,
  '85312': 52,
  '85318': 52,
  '85323': 52,
  '85329': 52,
  '85335': 52,
  '85338': 52,
  '85339': 52,
  '85340': 52,
  '85345': 52,
  '85351': 52,
  '85353': 52,
  '85355': 52,
  '85363': 52,
  '85372': 52,
  '85373': 52,
  '85374': 52,
  '85375': 52,
  '85376': 52,
  '85378': 52,
  '85379': 52,
  '85380': 52,
  '85381': 52,
  '85382': 52,
  '85383': 52,
  '85385': 52,
  '85387': 52,
  '85388': 52,
  '85392': 52,
  '85395': 52,

  // Mesa, AZ (54) — overlaps with Glendale on 85040–85042/85045/85048
  '85044': 54,
  '85118': 54,
  '85119': 54,
  '85120': 54,
  '85138': 54,
  '85140': 54,
  '85142': 54,
  '85143': 54,
  '85201': 54,
  '85202': 54,
  '85203': 54,
  '85204': 54,
  '85205': 54,
  '85206': 54,
  '85207': 54,
  '85208': 54,
  '85209': 54,
  '85210': 54,
  '85211': 54,
  '85212': 54,
  '85213': 54,
  '85214': 54,
  '85215': 54,
  '85216': 54,
  '85217': 54,
  '85218': 54,
  '85219': 54,
  '85220': 54,
  '85221': 54,
  '85224': 54,
  '85225': 54,
  '85226': 54,
  '85227': 54,
  '85233': 54,
  '85234': 54,
  '85236': 54,
  '85242': 54,
  '85244': 54,
  '85246': 54,
  '85247': 54,
  '85248': 54,
  '85249': 54,
  '85250': 54,
  '85251': 54,
  '85252': 54,
  '85255': 54,
  '85256': 54,
  '85257': 54,
  '85258': 54,
  '85259': 54,
  '85260': 54,
  '85261': 54,
  '85262': 54,
  '85263': 54,
  '85264': 54,
  '85266': 54,
  '85267': 54,
  '85268': 54,
  '85269': 54,
  '85271': 54,
  '85274': 54,
  '85275': 54,
  '85277': 54,
  '85278': 54,
  '85280': 54,
  '85281': 54,
  '85282': 54,
  '85283': 54,
  '85284': 54,
  '85285': 54,
  '85286': 54,
  '85287': 54,
  '85290': 54,
  '85295': 54,
  '85296': 54,
  '85297': 54,
  '85298': 54,

  // Phoenix, AZ (56) — Phoenix overrides Glendale (52) on shared AZ ZIPs
  // and overrides Mesa (54) on 85040/85041/85042
  '85040': 56,
  '85041': 56,
  '85042': 56,
  '85050': 56,
  '85054': 56,
  '85055': 56,
  '85086': 56,
  '85087': 56,
  '85098': 56,
  '85099': 56,
  '85254': 56,
  '85326': 56,
  // (Other Phoenix ZIPs already covered by Glendale list; keep
  // explicit overrides where Phoenix is canonical.)

  // Pleasant Hill, CA (58)
  '94503': 58,
  '94506': 58,
  '94507': 58,
  '94510': 58,
  '94512': 58,
  '94517': 58,
  '94518': 58,
  '94519': 58,
  '94520': 58,
  '94521': 58,
  '94523': 58,
  '94525': 58,
  '94526': 58,
  '94528': 58,
  '94533': 58,
  '94534': 58,
  '94535': 58,
  '94547': 58,
  '94549': 58,
  '94553': 58,
  '94558': 58,
  '94559': 58,
  '94564': 58,
  '94565': 58,
  '94569': 58,
  '94572': 58,
  '94581': 58,
  '94582': 58,
  '94585': 58,
  '94589': 58,
  '94590': 58,
  '94591': 58,
  '94592': 58,
  '94595': 58,
  '94596': 58,
  '94597': 58,
  '94598': 58,
  '94803': 58,
  '95687': 58,

  // Reno, NV (60)
  '89402': 60,
  '89403': 60,
  '89413': 60,
  '89423': 60,
  '89428': 60,
  '89429': 60,
  '89431': 60,
  '89432': 60,
  '89433': 60,
  '89434': 60,
  '89435': 60,
  '89436': 60,
  '89439': 60,
  '89440': 60,
  '89441': 60,
  '89450': 60,
  '89451': 60,
  '89452': 60,
  '89501': 60,
  '89502': 60,
  '89503': 60,
  '89504': 60,
  '89505': 60,
  '89506': 60,
  '89507': 60,
  '89508': 60,
  '89509': 60,
  '89510': 60,
  '89511': 60,
  '89512': 60,
  '89513': 60,
  '89515': 60,
  '89519': 60,
  '89520': 60,
  '89521': 60,
  '89523': 60,
  '89533': 60,
  '89555': 60,
  '89557': 60,
  '89570': 60,
  '89595': 60,
  '89701': 60,
  '89702': 60,
  '89703': 60,
  '89704': 60,
  '89705': 60,
  '89706': 60,
  '89711': 60,
  '89712': 60,
  '89713': 60,
  '89714': 60,
  '89721': 60,
  '96111': 60,
  '96126': 60,
  '96140': 60,
  '96143': 60,
  '96145': 60,
  '96146': 60,
  '96148': 60,
  '96160': 60,
  '96161': 60,
  '96162': 60,

  // San Jose, CA (62)
  '94020': 62,
  '94021': 62,
  '94022': 62,
  '94023': 62,
  '94024': 62,
  '94025': 62,
  '94026': 62,
  '94027': 62,
  '94028': 62,
  '94035': 62,
  '94039': 62,
  '94040': 62,
  '94041': 62,
  '94042': 62,
  '94043': 62,
  '94061': 62,
  '94062': 62,
  '94063': 62,
  '94064': 62,
  '94085': 62,
  '94086': 62,
  '94087': 62,
  '94088': 62,
  '94089': 62,
  '94301': 62,
  '94302': 62,
  '94303': 62,
  '94304': 62,
  '94305': 62,
  '94306': 62,
  '94309': 62,
  '95001': 62,
  '95002': 62,
  '95003': 62,
  '95004': 62,
  '95005': 62,
  '95006': 62,
  '95007': 62,
  '95008': 62,
  '95009': 62,
  '95010': 62,
  '95011': 62,
  '95013': 62,
  '95014': 62,
  '95015': 62,
  '95017': 62,
  '95018': 62,
  '95019': 62,
  '95020': 62,
  '95021': 62,
  '95026': 62,
  '95030': 62,
  '95031': 62,
  '95032': 62,
  '95033': 62,
  '95035': 62,
  '95036': 62,
  '95037': 62,
  '95038': 62,
  '95041': 62,
  '95042': 62,
  '95044': 62,
  '95046': 62,
  '95050': 62,
  '95051': 62,
  '95052': 62,
  '95053': 62,
  '95054': 62,
  '95055': 62,
  '95056': 62,
  '95060': 62,
  '95061': 62,
  '95062': 62,
  '95063': 62,
  '95064': 62,
  '95065': 62,
  '95066': 62,
  '95067': 62,
  '95070': 62,
  '95071': 62,
  '95073': 62,
  '95076': 62,
  '95077': 62,
  '95101': 62,
  '95103': 62,
  '95106': 62,
  '95108': 62,
  '95109': 62,
  '95110': 62,
  '95111': 62,
  '95112': 62,
  '95113': 62,
  '95115': 62,
  '95116': 62,
  '95117': 62,
  '95118': 62,
  '95119': 62,

  // San Leandro, CA (63) — overrides San Jose on shared East Bay ZIPs
  '94502': 63,
  '94536': 63,
  '94538': 63,
  '94539': 63,
  '94540': 63,
  '94541': 63,
  '94542': 63,
  '94543': 63,
  '94544': 63,
  '94545': 63,
  '94546': 63,
  '94550': 63,
  '94551': 63,
  '94552': 63,
  '94555': 63,
  '94556': 63,
  '94557': 63,
  '94560': 63,
  '94566': 63,
  '94568': 63,
  '94577': 63,
  '94578': 63,
  '94579': 63,
  '94580': 63,
  '94583': 63,
  '94586': 63,
  '94587': 63,
  '94588': 63,
  '94601': 63,
  '94603': 63,
  '94605': 63,
  '94606': 63,
  '94613': 63,
  '94614': 63,
  '94619': 63,
  '94620': 63,
  '94621': 63,
  '94624': 63,

  // Spokane, WA (64)
  '83814': 64,
  '83815': 64,
  '83835': 64,
  '83854': 64,
  '83858': 64,
  '83869': 64,
  '83876': 64,
  '83877': 64,
  '99001': 64,
  '99003': 64,
  '99004': 64,
  '99005': 64,
  '99006': 64,
  '99008': 64,
  '99009': 64,
  '99011': 64,
  '99012': 64,
  '99013': 64,
  '99014': 64,
  '99016': 64,
  '99018': 64,
  '99019': 64,
  '99020': 64,
  '99021': 64,
  '99022': 64,
  '99023': 64,
  '99025': 64,
  '99026': 64,
  '99027': 64,
  '99029': 64,
  '99030': 64,
  '99031': 64,
  '99034': 64,
  '99036': 64,
  '99037': 64,
  '99039': 64,
  '99110': 64,
  '99170': 64,
  '99201': 64,
  '99202': 64,
  '99203': 64,
  '99204': 64,
  '99205': 64,
  '99206': 64,
  '99207': 64,
  '99208': 64,
  '99209': 64,
  '99210': 64,
  '99211': 64,
  '99212': 64,
  '99213': 64,
  '99214': 64,
  '99215': 64,
  '99216': 64,
  '99217': 64,
  '99218': 64,
  '99219': 64,
  '99220': 64,
  '99223': 64,
  '99224': 64,
  '99228': 64,
  '99251': 64,
  '99252': 64,
  '99256': 64,
  '99258': 64,
  '99260': 64,
  '99299': 64,

  // Stockton, CA (65)
  '95202': 65,
  '95203': 65,
  '95204': 65,
  '95205': 65,
  '95206': 65,
  '95207': 65,
  '95208': 65,
  '95209': 65,
  '95210': 65,
  '95211': 65,
  '95212': 65,
  '95215': 65,
  '95219': 65,
  '95220': 65,
  '95225': 65,
  '95227': 65,
  '95230': 65,
  '95231': 65,
  '95236': 65,
  '95237': 65,
  '95240': 65,
  '95241': 65,
  '95242': 65,
  '95252': 65,
  '95253': 65,
  '95254': 65,
  '95258': 65,
  '95267': 65,
  '95269': 65,
  '95297': 65,
  '95304': 65,
  '95320': 65,
  '95330': 65,
  '95336': 65,
  '95337': 65,
  '95350': 65,
  '95356': 65,
  '95358': 65,
  '95366': 65,
  '95367': 65,
  '95368': 65,
  '95376': 65,
  '95377': 65,
  '95378': 65,
  '95385': 65,
  '95391': 65,
  '95397': 65,
  '95632': 65,
  '95638': 65,

  // Tucson, AZ (66)
  '85614': 66,
  '85619': 66,
  '85622': 66,
  '85623': 66,
  '85629': 66,
  '85641': 66,
  '85652': 66,
  '85653': 66,
  '85654': 66,
  '85701': 66,
  '85702': 66,
  '85703': 66,
  '85704': 66,
  '85705': 66,
  '85706': 66,
  '85707': 66,
  '85708': 66,
  '85709': 66,
  '85710': 66,
  '85711': 66,
  '85712': 66,
  '85713': 66,
  '85714': 66,
  '85715': 66,
  '85716': 66,
  '85717': 66,
  '85718': 66,
  '85719': 66,
  '85721': 66,
  '85722': 66,
  '85723': 66,
  '85724': 66,
  '85725': 66,
  '85726': 66,
  '85728': 66,
  '85730': 66,
  '85731': 66,
  '85732': 66,
  '85733': 66,
  '85734': 66,
  '85735': 66,
  '85736': 66,
  '85737': 66,
  '85738': 66,
  '85739': 66,
  '85740': 66,
  '85741': 66,
  '85742': 66,
  '85743': 66,
  '85744': 66,
  '85745': 66,
  '85746': 66,
  '85747': 66,
  '85748': 66,
  '85749': 66,
  '85750': 66,
  '85751': 66,
  '85752': 66,
  '85754': 66,
  '85755': 66,
  '85756': 66,
  '85757': 66,
  '85775': 66,

  // Las Vegas, NV (53)
  '89012': 53,
  '89014': 53,
  '89015': 53,
  '89030': 53,
  '89031': 53,
  '89032': 53,
  '89033': 53,
  '89052': 53,
  '89074': 53,
  '89101': 53,
  '89102': 53,
  '89103': 53,
  '89104': 53,
  '89106': 53,
  '89107': 53,
  '89108': 53,
  '89109': 53,
  '89110': 53,
  '89113': 53,
  '89115': 53,
  '89117': 53,
  '89118': 53,
  '89119': 53,
  '89120': 53,
  '89121': 53,
  '89122': 53,
  '89123': 53,
  '89124': 53,
  '89128': 53,
  '89130': 53,
  '89134': 53,
  '89135': 53,
  '89139': 53,
  '89141': 53,
  '89142': 53,
  '89144': 53,
  '89145': 53,
  '89146': 53,
  '89147': 53,
  '89148': 53,
  '89154': 53,
  '89156': 53,
  '89165': 53,
  '89169': 53,
  '89178': 53,
  '89179': 53,
  '89183': 53,

  // Bakersfield, CA (195)
  '93201': 195,
  '93203': 195,
  '93206': 195,
  '93215': 195,
  '93216': 195,
  '93218': 195,
  '93219': 195,
  '93220': 195,
  '93241': 195,
  '93250': 195,
  '93252': 195,
  '93256': 195,
  '93261': 195,
  '93263': 195,
  '93268': 195,
  '93276': 195,
  '93280': 195,
  '93287': 195,
  '93301': 195,
  '93302': 195,
  '93303': 195,
  '93304': 195,
  '93305': 195,
  '93306': 195,
  '93307': 195,
  '93308': 195,
  '93309': 195,
  '93311': 195,
  '93312': 195,
  '93313': 195,
  '93314': 195,
  '93380': 195,
  '93383': 195,
  '93384': 195,
  '93385': 195,
  '93386': 195,
  '93387': 195,
  '93388': 195,
  '93389': 195,
  '93390': 195,
  '93531': 195,

  // Fresno, CA (196)
  '93601': 196,
  '93606': 196,
  '93610': 196,
  '93611': 196,
  '93612': 196,
  '93613': 196,
  '93614': 196,
  '93616': 196,
  '93619': 196,
  '93622': 196,
  '93625': 196,
  '93626': 196,
  '93636': 196,
  '93637': 196,
  '93638': 196,
  '93639': 196,
  '93645': 196,
  '93648': 196,
  '93650': 196,
  '93651': 196,
  '93652': 196,
  '93653': 196,
  '93657': 196,
  '93662': 196,
  '93701': 196,
  '93702': 196,
  '93703': 196,
  '93704': 196,
  '93705': 196,
  '93706': 196,
  '93707': 196,
  '93708': 196,
  '93709': 196,
  '93710': 196,
  '93711': 196,
  '93712': 196,
  '93714': 196,
  '93715': 196,
  '93716': 196,
  '93717': 196,
  '93718': 196,
  '93720': 196,
  '93721': 196,
  '93722': 196,
  '93723': 196,
  '93724': 196,
  '93725': 196,
  '93726': 196,
  '93727': 196,
  '93728': 196,
  '93729': 196,
  '93730': 196,
  '93737': 196,
  '93740': 196,
  '93741': 196,
  '93744': 196,
  '93745': 196,
  '93747': 196,
  '93750': 196,
  '93755': 196,
  '93760': 196,
  '93761': 196,
  '93764': 196,
  '93765': 196,
  '93771': 196,
  '93772': 196,
  '93773': 196,
  '93774': 196,
  '93775': 196,
  '93776': 196,
  '93777': 196,
  '93778': 196,
  '93779': 196,
  '93786': 196,
  '93790': 196,
  '93791': 196,
  '93792': 196,
  '93793': 196,
  '93794': 196,
  '93844': 196,
  '93888': 196,
  '95301': 196,
  '95306': 196,
  '95317': 196,
  '95333': 196,
  '95338': 196,
  '95340': 196,
  '95341': 196,
  '95343': 196,
  '95344': 196,
  '95348': 196,

  // Hesperia, CA (197)
  '91759': 197,
  '92301': 197,
  '92307': 197,
  '92308': 197,
  '92317': 197,
  '92321': 197,
  '92322': 197,
  '92325': 197,
  '92329': 197,
  '92340': 197,
  '92342': 197,
  '92344': 197,
  '92345': 197,
  '92352': 197,
  '92358': 197,
  '92368': 197,
  '92371': 197,
  '92372': 197,
  '92378': 197,
  '92391': 197,
  '92392': 197,
  '92393': 197,
  '92394': 197,
  '92395': 197,
  '92397': 197,
  '92407': 197,
  '93544': 197,

  // Lancaster, CA (198) — Lancaster overrides Hesperia on 93544
  '91390': 198,
  '93243': 198,
  '93501': 198,
  '93510': 198,
  '93524': 198,
  '93534': 198,
  '93535': 198,
  '93536': 198,
  '93539': 198,
  '93543': 198,
  '93544': 198,
  '93550': 198,
  '93551': 198,
  '93552': 198,
  '93560': 198,
  '93563': 198,
  '93584': 198,
  '93586': 198,
  '93590': 198,
  '93591': 198,
  '93599': 198,

  // Modesto, CA (199) — Modesto overrides Stockton on shared Central Valley ZIPs
  '95208': 199,
  '95307': 199,
  '95313': 199,
  '95316': 199,
  '95319': 199,
  '95320': 199,
  '95324': 199,
  '95326': 199,
  '95328': 199,
  '95336': 199,
  '95337': 199,
  '95350': 199,
  '95351': 199,
  '95352': 199,
  '95353': 199,
  '95354': 199,
  '95355': 199,
  '95356': 199,
  '95357': 199,
  '95358': 199,
  '95361': 199,
  '95363': 199,
  '95366': 199,
  '95367': 199,
  '95368': 199,
  '95380': 199,
  '95381': 199,
  '95382': 199,
  '95385': 199,
  '95386': 199,
  '95387': 199,
  '95397': 199,

  // Ontario, CA (200)
  '91701': 200,
  '91708': 200,
  '91710': 200,
  '91711': 200,
  '91729': 200,
  '91730': 200,
  '91737': 200,
  '91739': 200,
  '91743': 200,
  '91750': 200,
  '91752': 200,
  '91758': 200,
  '91761': 200,
  '91762': 200,
  '91763': 200,
  '91764': 200,
  '91766': 200,
  '91767': 200,
  '91768': 200,
  '91769': 200,
  '91784': 200,
  '91785': 200,
  '91786': 200,
  '92316': 200,
  '92331': 200,
  '92334': 200,
  '92335': 200,
  '92337': 200,
  '92376': 200,
  '92377': 200,
  '92501': 200,
  '92502': 200,
  '92503': 200,
  '92505': 200,
  '92509': 200,
  '92513': 200,
  '92514': 200,
  '92515': 200,
  '92516': 200,
  '92517': 200,
  '92519': 200,
  '92522': 200,
  '92823': 200,
  '92860': 200,
  '92877': 200,
  '92878': 200,
  '92879': 200,
  '92880': 200,

  // Rancho Mirage, CA (201)
  '92201': 201,
  '92202': 201,
  '92203': 201,
  '92210': 201,
  '92211': 201,
  '92230': 201,
  '92234': 201,
  '92235': 201,
  '92236': 201,
  '92240': 201,
  '92241': 201,
  '92247': 201,
  '92248': 201,
  '92252': 201,
  '92253': 201,
  '92255': 201,
  '92256': 201,
  '92258': 201,
  '92260': 201,
  '92261': 201,
  '92262': 201,
  '92263': 201,
  '92264': 201,
  '92270': 201,
  '92276': 201,
  '92282': 201,
  '92284': 201,
  '92286': 201,
  '92539': 201,
  '92549': 201,
  '92561': 201,

  // Santa Maria, CA (202)
  '93420': 202,
  '93421': 202,
  '93427': 202,
  '93429': 202,
  '93433': 202,
  '93434': 202,
  '93436': 202,
  '93437': 202,
  '93438': 202,
  '93440': 202,
  '93441': 202,
  '93444': 202,
  '93445': 202,
  '93448': 202,
  '93449': 202,
  '93454': 202,
  '93455': 202,
  '93456': 202,
  '93457': 202,
  '93458': 202,
  '93460': 202,
  '93463': 202,
  '93464': 202,
  '93475': 202,
  '93483': 202,

  // Visalia, CA (204) — overrides Bakersfield on 93256 and Fresno on 93662
  '93202': 204,
  '93204': 204,
  '93208': 204,
  '93210': 204,
  '93212': 204,
  '93221': 204,
  '93223': 204,
  '93226': 204,
  '93227': 204,
  '93230': 204,
  '93232': 204,
  '93234': 204,
  '93235': 204,
  '93239': 204,
  '93242': 204,
  '93244': 204,
  '93245': 204,
  '93246': 204,
  '93247': 204,
  '93256': 204,
  '93257': 204,
  '93258': 204,
  '93260': 204,
  '93265': 204,
  '93266': 204,
  '93267': 204,
  '93270': 204,
  '93272': 204,
  '93274': 204,
  '93275': 204,
  '93277': 204,
  '93278': 204,
  '93279': 204,
  '93282': 204,
  '93286': 204,
  '93291': 204,
  '93292': 204,
  '93615': 204,
  '93618': 204,
  '93631': 204,
  '93647': 204,
  '93656': 204,
  '93662': 204,
  '93666': 204,
  '93670': 204,
  '93673': 204,

  // Temecula, CA (203)
  '92003': 203,
  '92026': 203,
  '92028': 203,
  '92059': 203,
  '92061': 203,
  '92082': 203,
  '92083': 203,
  '92084': 203,
  '92088': 203,
  '92518': 203,
  '92530': 203,
  '92531': 203,
  '92532': 203,
  '92536': 203,
  '92543': 203,
  '92544': 203,
  '92545': 203,
  '92546': 203,
  '92548': 203,
  '92551': 203,
  '92552': 203,
  '92555': 203,
  '92562': 203,
  '92563': 203,
  '92564': 203,
  '92567': 203,
  '92570': 203,
  '92571': 203,
  '92572': 203,
  '92581': 203,
  '92582': 203,
  '92583': 203,
  '92584': 203,
  '92585': 203,
  '92586': 203,
  '92587': 203,
  '92589': 203,
  '92590': 203,
  '92591': 203,
  '92592': 203,
  '92593': 203,
  '92595': 203,
  '92596': 203,
  '92599': 203,
}

// Online campus is the fallback when an entered ZIP doesn't map to any
// physical campus service area.
const ONLINE_CAMPUS_ID = 55

export const getCarringtonCampusByZip = (
  zip: string,
): CarringtonCampusDef | null => {
  const campusId = CARRINGTON_ZIP_TO_CAMPUS[zip] ?? ONLINE_CAMPUS_ID
  return CARRINGTON_CAMPUSES.find((c) => c.id === campusId) || null
}

export const getCarringtonProgramsForCampus = (
  campusId: number,
): UniversityProgram[] => {
  const campus = CARRINGTON_CAMPUSES.find((c) => c.id === campusId)
  if (!campus) return []
  const seen = new Set<string>()
  const out: UniversityProgram[] = []
  for (const rawId of campus.programIds) {
    const id = resolveProgramId(rawId)
    const def = PROGRAM_BY_ID[id]
    if (!def) continue
    if (seen.has(def.name)) continue
    seen.add(def.name)
    out.push({
      name: def.name,
      category: def.category,
      degreeLevel: def.degreeLevel,
      slug: def.slug,
    })
  }
  return out
}

// Campus directory display data (campus name → readable program list)
export const getCarringtonCampusDirectory = (): {
  id: number
  name: string
  programs: string[]
}[] => {
  return CARRINGTON_CAMPUSES.map((c) => {
    const seen = new Set<string>()
    const programs: string[] = []
    for (const rawId of c.programIds) {
      const id = resolveProgramId(rawId)
      const def = PROGRAM_BY_ID[id]
      if (!def) continue
      if (seen.has(def.name)) continue
      seen.add(def.name)
      programs.push(def.name)
    }
    return {
      id: c.id,
      name: c.name,
      programs,
    }
  })
}
